<template>
  <div
    class="modal-dialog modal-dialog-centered modal-xl modal-fullscreen-lg-down"
  >
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <template v-if="isEdit">
            Edit
          </template>
          <template v-else>
            Add
          </template>
          <template v-if="isClassEvent">
            Class
          </template>
          <template v-else>
            Background
          </template>
          Event
        </h5>
        <button
          type="button"
          class="btn-close"
          aria-label="Close"
          @click="close"
        />
      </div>

      <div class="modal-body">
        <form v-if="event">
          <div class="main-container g-0">
            <div v-if="isEdit && !isClassEvent" class="row">
              <div class="col mb-2">
                <label for="class" class="col-form-label">Event Type</label>
                <SelectEventType v-model="event.event_type" class="nav nav-pills nav-fill" />
              </div>
            </div>

            <div class="row">
              <div v-if="isClassEvent && event && 'school_class' in event" class="col-12 col-lg-6">
                <label for="class" class="col-form-label"> Class </label>
                <div class="input-group">
                  <SelectInput
                    id="class"
                    v-model="event.school_class"
                    :options="activeClassChoices"
                    query-field-name="queryString"
                    :show-clear-button="false"
                    :class="{ 'is-invalid': inputErrors['school_class'] }"
                    placeholder="Select a class..."
                  />
                  <div class="invalid-feedback">
                    {{ inputErrors["school_class"] }}
                  </div>
                </div>
              </div>

              <div class="col-12 col-lg-6">
                <label for="class" class="col-form-label"> Teacher </label>
                <SelectInput
                  id="class"
                  v-model="event.teacher"
                  :options="teacherChoices"
                  :show-clear-button="false"
                  :class="{ 'is-invalid': inputErrors['teacher'] }"
                  placeholder="Select a teacher..."
                />
                <div class="invalid-feedback">
                  {{ inputErrors["teacher"] }}
                </div>
              </div>
              <div class="col-12">
                <div v-if="showDifferentTeacherWarning" class="alert alert-warning p-1 mt-2" role="alert">
                  The selected teacher (<strong>{{ getMapping("teacher", event.teacher).title }}</strong>) is covering
                  the class teacher (<strong>{{ schoolClassTeacher.title }}</strong>).
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-12 col-sm">
                <label class="col-form-label"> Date </label>
                <VueDatePicker
                  v-model="startDate"
                  input-class-name="form-control"
                  placeholder="Select date"
                  :format="formatDate"
                  :enable-time-picker="false"
                  :clearable="false"
                  :disabled-week-days="[6, 0]"
                  hide-input-icon
                  auto-apply
                  :teleport="true"
                />
              </div>
              <div class="col-12 col-sm mt-2 mt-sm-0">
                <div class="d-flex class_event_time_range">
                  <div>
                    <label for="first_name" class="col-form-label"> Start </label>
                    <Timepicker v-model="startTime" :min="8" :max="21" />
                  </div>

                  <div>
                    <label for="first_name" class="col-form-label"> End </label>
                    <Timepicker v-model="endTime" :min="startDate" :max="21" />
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col">
                <label for="notes" class="col-form-label">Notes</label>
                <textarea
                  id="notes"
                  v-model="event.notes"
                  class="form-control"
                  rows="2"
                />
              </div>
            </div>

            <div v-if="schoolClass?.google_meets_url" class="row">
              <div class="col">
                <label for="notes" class="col-form-label me-2">Google Meets URL</label>
                <a :href="schoolClass?.google_meets_url" target="_blank">
                  {{ schoolClass?.google_meets_url }}
                </a>
              </div>
            </div>

            <template v-if="isClassEvent && event && 'attendances' in event && event.school_class">
              <div class="row">
                <div class="col">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0">
                      Class Event Students
                    </h6>
                    <button
                      type="button"
                      class="btn btn-outline-primary"
                      @click="openSelectStudentsModal"
                    >
                      <i class="bi bi-plus-lg" /> Select Students
                    </button>
                  </div>
                  <div
                    v-if="event.attendances.length"
                    class="attendance_table_wrapper"
                  >
                    <EventAttendanceTable v-model="event" />
                  </div>
                  <div v-else class="text-light-emphasis text-center py-2">
                    Currently there are no students in this class.<br>
                    Click the <strong>Select Students</strong> button to add them.
                  </div>
                </div>
              </div>
            </template>
          </div>
        </form>

        <div v-if="inputErrors['non_field_errors'] || inputErrors['attendances']" class="alert alert-danger p-1 mb-0" role="alert">
          {{ inputErrors["non_field_errors"] || inputErrors['attendances'] }}
        </div>
      </div>
      <div class="modal-footer">
        <div v-if="showOnlyOneStudentWarning" class="w-100">
          <div class="alert alert-warning p-1 mb-0" role="alert">
            <!-- TODO could open class modal from here instead of redirecting to classes -->
            The selected class is a group class but it only has one student <br>
            Check in
            <a href="/classes">Classes</a>
            page if it is supposed to be an individual class.
          </div>
        </div>
        <div v-if="showManyStudentWarning" class="w-100">
          <div class="alert alert-warning p-1 mb-0" role="alert">
            <!-- TODO could open class modal from here instead of redirecting to classes -->
            The selected class is an individual class but it has more than one student. <br>
            Check in
            <a href="/classes">Classes</a>
            page if it is supposed to be a group class <strong>(change Class Format to Group)</strong>.
          </div>
        </div>
        <div v-if="isEdit" class="me-auto">
          <button type="button" class="btn btn-danger" @click="confirmDeleteClassEvent">
            Delete
          </button>
        </div>
        <button type="button" class="btn btn-secondary" @click="close">
          Cancel
        </button>
        <button type="button" class="btn btn-primary" @click="saveEvent">
          Save
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { api } from "@/api";
import { defineComponent } from "vue";
import mappingsMixin from "@/mixins/mappings.mixin";
import SelectInput from "@/components/ui/input/SelectInput.vue";
import ISelectInputChoice, { IInputErrors } from "@/types/input";
import { useUserStore } from "@/stores/user.store";
import { useCalendarStore } from "@/stores/calendar.store";
import Timepicker from "@/components/ui/timepicker/Timepicker.vue";
import timeMixin from "@/mixins/time.mixin";
import { useToastStore } from "@/stores/toast.store";
import { useModalStore } from "@/stores/modal.store";
import { watch, ref, onBeforeUnmount } from "vue";
import EventAttendanceTable from "@/components/modals/calendar/event/EventAttendanceTable.vue";
import IStudentEventAttendance from "@/types/student_event_attendance";
import ISchoolClass from "@/types/school_class";
import SelectEventType from "@/components/modals/calendar/event/SelectEventType.vue";
import { EventType } from "@/types/enums/event_type";
import CalendarEvent from "@/types/models/calendar_event";
import CalendarClassEvent from "@/types/models/calendar_class_event";
import { useStudentStore } from "@/stores/student.store";
import useDateMixin from "@/mixins/date.mixin";


export default defineComponent({
  name: "EventModal",
  components: { SelectEventType, SelectInput, Timepicker, EventAttendanceTable },
  mixins: [mappingsMixin, timeMixin],
  props: {
    dateEventInfo: {
      type: Object,
      default: () => {
        return null;
      },
    },
  },
  emits: ["close"],
  setup(props) {
    const userStore = useUserStore();
    const studentStore = useStudentStore();
    const calendarStore = useCalendarStore();
    const toastStore = useToastStore();
    const { formatDate } = useDateMixin();
    const modalStore = useModalStore();
    const event = ref<CalendarEvent | CalendarClassEvent>(calendarStore.getEventFromFullCalendarEvent(props.dateEventInfo));
    const inputErrors = ref({} as IInputErrors);

    // Watch for changes in the dateEventInfo prop
    const stopDateEventInfoWatch = watch(
      () => props.dateEventInfo,
      (newDateEventInfo) => {
        // React to changes here
        event.value = calendarStore.getEventFromFullCalendarEvent(newDateEventInfo);
      },
    );

    function isCalendarClassEvent(event: CalendarEvent | CalendarClassEvent): event is CalendarClassEvent {
      return (event as CalendarClassEvent).school_class !== undefined;
    }
    // New watcher for event.school_class
    const stopEventSchoolClassWatch = watch(
      () => isCalendarClassEvent(event.value) ? event.value.school_class : undefined,
      () => {
        // React to changes in event.school_class here
        if (isCalendarClassEvent(event.value)) {
          inputErrors.value["school_class"] = event.value.validate_school_class();
        }
      },
      {
        deep: true,
      },
    );
    // New watcher for event.school_class
    const stopEventTeacherWatch = watch(
      () =>event.value.teacher,
      () => {
        inputErrors.value["teacher"] = event.value.validate_teacher();
      },
      {
        deep: true,
      },
    );

    onBeforeUnmount(() => {
      // Cleanup logic, e.g., stopping the watcher
      stopDateEventInfoWatch();
      stopEventSchoolClassWatch();
      stopEventTeacherWatch();
    });

    return { userStore, calendarStore, studentStore, toastStore, formatDate, modalStore, event, inputErrors };
  },
  computed: {
    isEdit() {
      return this.event?.id != null;
    },
    teacherChoices() {
      return this.getChoices("teacher").filter(teacher => teacher.is_active);
    },
    isClassEvent() {
      return this.event?.event_type === EventType.CLASS;
    },
    activeClassChoices() {
      return (this.getChoices("school_class") as (ISelectInputChoice & ISchoolClass)[])
        .filter((schoolClass) => schoolClass.is_active);
    },
    startDate: {
      get() {
        return new Date(this.event.start);
      },
      set(newDate: Date) {
        const startDate = new Date(this.event.start);

        // Set all three at once: setting the month alone overflows on the 29th-31st.
        startDate.setFullYear(newDate.getFullYear(), newDate.getMonth(), newDate.getDate());
        this.event.start = startDate.toISOString();

        // Also update the end date to match the start date.
        const endDate = new Date(this.event.end);
        endDate.setFullYear(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        this.event.end = endDate.toISOString();
      },
    },
    startTime: {
      get() {
        return new Date(this.event.start);
      },
      set(newTime: string) {
        const startDate = new Date(this.event.start);
        const [hours, minutes] = newTime.split(":");
        this.event.start = this.setTimeOnDate(startDate, hours, minutes).toISOString();
      },
    },
    endTime: {
      get() {
        return new Date(this.event.end);
      },
      set(newTime: string) {
        const endDate = new Date(this.event.end);
        const [hours, minutes] = newTime.split(":");
        this.event.end = this.setTimeOnDate(endDate, hours, minutes).toISOString();
      },
    },
    schoolClass(): ISchoolClass | null {
      if ("school_class" in this.event) {
        return this.event.school_class ? this.getMapping("school_class", this.event.school_class) : undefined;
      }
      return null
    },
    schoolClassTeacher() {
      return this.schoolClass ? this.getMapping("teacher", this.schoolClass?.teacher) : undefined;
    },
    attendanceStudentIds(): number[] {
      if ("attendances" in this.event) {
        return this.event?.attendances.map(attendance => attendance.student);
      }
      return []
    },
    showDifferentTeacherWarning() {
      if (!this.schoolClass?.teacher || !this.event?.teacher) return false;

      return this.event.teacher != this.schoolClass?.teacher;
    },
    showOnlyOneStudentWarning(): boolean {
      // Show warning if only 1 student and class is not individual or pair class.
      return !this.schoolClass?.is_individual && !this.schoolClass?.is_pair && this.schoolClass?.students?.length === 1;
    },
    showManyStudentWarning(): boolean {
      // Show warning if many students and class is individual (not for pair classes)
      return !!this.schoolClass?.is_individual && !this.schoolClass?.is_pair && this.schoolClass?.students.length > 1;
    },
  },
  watch: {
    schoolClass(newSchoolClass?: ISchoolClass | null, oldSchoolClass?: ISchoolClass | null) {
      // Only prepopulate when a different class was selected, not when
      // the cached classes were refreshed in the background.
      if (newSchoolClass?.id !== oldSchoolClass?.id) {
        this.prepopulateClassEventStudents()
      }
    },
  },
  methods: {
    openClassModal(schoolClass?: ISchoolClass) {
      this.modalStore.openSchoolClassModal(schoolClass);
    },
    async prepopulateClassEventStudents() {
      /**
       * If we are creating a new class event, we want to automatically populate the
       * student attendances from the selected school class's students
       **/
      if (this.schoolClass?.students && !this.isEdit && "attendances" in this.event) {
        const schoolClassId = this.schoolClass.id as number;
        let schoolClass = this.schoolClass;

        // The cached class can be stale (someone else could have changed its students
        // in the meantime), so fetch the latest class.
        try {
          schoolClass = await api.calendarApi.getSchoolClass(schoolClassId) as ISchoolClass;
        } catch (error: any) {
          console.error(error);
        }

        // A different class was selected in the meantime.
        if (!("school_class" in this.event) || this.event.school_class !== schoolClassId) return;

        // Sent with the new event, so the backend can reject it if the class changes before saving.
        this.event.school_class_updated = schoolClass.updated;
        this.event.attendances = schoolClass.students.map((studentId) => ({
          id: undefined,
          student: studentId,
          attended: false,
          no_show: false,
        } as IStudentEventAttendance));
      }
    },
    async saveEvent() {
      this.inputErrors = this.event.validate();

      if (Object.keys(this.inputErrors).length > 0) {
        return;
      }

      try {
        await this.calendarStore.saveEvent(this.event);
      } catch (error: any) {
        if (this.closeIfEventIsGone(error)) return;
        this.inputErrors = error;
        return;
      }

      // Re-fetch events to update events calendar.
      this.dateEventInfo.view.calendar.refetchEvents();
      this.close();
    },
    closeIfEventIsGone(error: any) {
      // 404: someone else deleted the event in the meantime.
      if (error?.status !== 404) return false;
      this.toastStore.errorToast("This event no longer exists.");
      this.dateEventInfo.view.calendar.refetchEvents();
      this.close();
      return true;
    },
    confirmDeleteClassEvent() {
      this.modalStore.openDeleteEventModal(this.event, this.deleteEvent);
    },
    async deleteEvent() {
      // Delete the existing class event.
      try {
        await api.calendarApi.deleteEvent(this.event?.id);
      } catch (error: any) {
        if (this.closeIfEventIsGone(error)) return;
        Object.keys(error).forEach(function (key) {
          error[key] = Array.isArray(error[key]) ? error[key].join("\n") : error[key];
        });
        this.inputErrors = error;
        return;
      }

      // Remove the event from the calendar.
      this.dateEventInfo.event.remove();

      // Show success message.
      this.toastStore.addToast("Class event was deleted successfully.");
      this.close();
    },
    selectedStudentsChanged(newStudentIds: number[]) {
      if (!("attendances" in this.event)) return [];

      // Filter existing attendances and discard those that were not selected.
      const existingAttendances = this.event.attendances.filter(
        attendance => newStudentIds.includes(attendance.student),
      );

      // Create new attendances for the students that were newly selected.
      const newAttendances = newStudentIds
        .filter((studentId) =>
          !existingAttendances.some((attendance) => attendance.student === studentId),
        )
        .map((studentId) => ({
          id: undefined,
          student: studentId,
          attended: false,
          no_show: false,
        } as IStudentEventAttendance));

      if ("attendances" in this.event) {
        this.event.attendances = existingAttendances.concat(newAttendances);
      }
    },
    openSelectStudentsModal() {
      this.modalStore.openSelectStudentsSubModal(
        this.attendanceStudentIds,
        this.selectedStudentsChanged,
      );
    },
    close() {
      this.$emit("close");
    },
  },
});
</script>
<style scoped lang="scss">
.class_event_time_range {
  gap: 12px;
  display: flex;
}
.attendance_table_wrapper {
  overflow: auto;
}
.modal-body {
  overflow: auto;
}
@include sm {
  .modal-content {
    max-height: 90vh;
  }
}
@include md {
  .modal-dialog.modal-xl {
    --bs-modal-width: 1040px;
  }
}
@include xl {
  .modal-dialog.modal-xl {
    --bs-modal-width: 1200px;
  }
}
:deep(.start_date) {
  max-width: 100%;
}
</style>
